import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, TextField, ThemeProvider, CircularProgress, Snackbar, InputAdornment, IconButton, Avatar } from '@material-ui/core';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { Icon } from 'antd';

import logo from './logo-color.png';

import { DEFAULT_MESSAGES } from '~/constants/default-messages.constants';
import { materialUITheme } from '~/themes/material-ui.theme';
import { MaterialUIFormControl } from '~/components/UI/Material/FormControl';
import { AccountStorage } from '~/storages/account';
import { loginWithAuth, login, checkEmail } from './api';
import { VerificacaoCodigo } from './VerificacaoCodigo';
import { Container, Form, Logo, LoadingContainer, ForgotPasswordLink } from './styles';

var RECENT_LOGINS_KEY = 'contlabs_recent_logins';

var getRecentLogins = function() {
    try {
        var data = localStorage.getItem(RECENT_LOGINS_KEY);
        return data ? JSON.parse(data) : [];
    } catch (e) {
        return [];
    }
};

var saveRecentLogin = function(userData) {
    try {
        var logins = getRecentLogins();
        // Remove existing entry for same email+tenant
        logins = logins.filter(function(l) {
            return !(l.email === userData.email && l.tenantId === userData.tenantId);
        });
        // Add to front
        logins.unshift({
            email: userData.email,
            name: userData.name,
            tenantId: userData.tenantId,
            tenantName: userData.tenantName,
            avatar: userData.avatar || null,
            savedAt: Date.now()
        });
        // Keep max 5
        if (logins.length > 5) logins = logins.slice(0, 5);
        localStorage.setItem(RECENT_LOGINS_KEY, JSON.stringify(logins));
    } catch (e) { /* ignore */ }
};

var removeRecentLogin = function(email, tenantId) {
    try {
        var logins = getRecentLogins();
        logins = logins.filter(function(l) {
            return !(l.email === email && l.tenantId === tenantId);
        });
        localStorage.setItem(RECENT_LOGINS_KEY, JSON.stringify(logins));
    } catch (e) { /* ignore */ }
};

// Dispositivos que passaram pelo segundo fator e pediram para ser lembrados
// (30 dias). Um por e-mail + escritório; o backend confere e ignora vencido.
var DISPOSITIVOS_KEY = 'contlabs_dispositivos';

var getDispositivo = function(email, tenantId) {
    try {
        var mapa = JSON.parse(localStorage.getItem(DISPOSITIVOS_KEY) || '{}');
        return mapa[email + '|' + tenantId] || null;
    } catch (e) {
        return null;
    }
};

var saveDispositivo = function(email, tenantId, token) {
    try {
        var mapa = JSON.parse(localStorage.getItem(DISPOSITIVOS_KEY) || '{}');
        mapa[email + '|' + tenantId] = token;
        localStorage.setItem(DISPOSITIVOS_KEY, JSON.stringify(mapa));
    } catch (e) { /* ignore */ }
};

var getInitials = function(name) {
    if (!name) return '?';
    var parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
};

var getAvatarColor = function(name) {
    if (!name) return '#722ed1';
    var colors = ['#722ed1', '#1890ff', '#52c41a', '#fa8c16', '#eb2f96', '#13c2c2', '#2f54eb', '#fa541c'];
    var hash = 0;
    for (var i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

var RecentLoginCard = function(props) {
    var item = props.item;
    var onSelect = props.onSelect;
    var onRemove = props.onRemove;

    return React.createElement('div', {
        onClick: function() { onSelect(item); },
        style: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            background: '#fff',
            borderRadius: 8,
            cursor: 'pointer',
            border: '1px solid #e8e8e8',
            transition: 'all 0.2s',
            position: 'relative',
        },
        onMouseEnter: function(e) {
            e.currentTarget.style.borderColor = '#722ed1';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(114, 46, 209, 0.12)';
        },
        onMouseLeave: function(e) {
            e.currentTarget.style.borderColor = '#e8e8e8';
            e.currentTarget.style.boxShadow = 'none';
        }
    },
        React.createElement(Avatar, {
            style: {
                backgroundColor: getAvatarColor(item.name),
                width: 38,
                height: 38,
                fontSize: 14,
                fontWeight: 600
            }
        }, getInitials(item.name)),
        React.createElement('div', { style: { flex: 1, minWidth: 0 } },
            React.createElement('div', {
                style: { fontSize: 14, fontWeight: 500, color: '#262626', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
            }, item.name),
            React.createElement('div', {
                style: { fontSize: 12, color: '#8c8c8c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
            }, item.tenantName || item.email)
        ),
        React.createElement('div', {
            onClick: function(e) {
                e.stopPropagation();
                onRemove(item);
            },
            style: {
                color: '#bfbfbf',
                fontSize: 12,
                cursor: 'pointer',
                padding: 4,
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            },
            onMouseEnter: function(e) { e.currentTarget.style.color = '#ff4d4f'; },
            onMouseLeave: function(e) { e.currentTarget.style.color = '#bfbfbf'; },
            title: 'Remover'
        }, React.createElement(Icon, { type: 'close' }))
    );
};

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [tenants, setTenants] = useState([]);
    const [selectedTenant, setSelectedTenant] = useState('');
    const [isCheckingEmail, setIsCheckingEmail] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [recentLogins, setRecentLogins] = useState(getRecentLogins());
    const [selectedRecent, setSelectedRecent] = useState(function() {
        var logins = getRecentLogins();
        return logins.length > 0 ? logins[0] : null;
    });
    const [showOtherAccounts, setShowOtherAccounts] = useState(false);
    const [desafio, setDesafio] = useState(null);
    const history = useHistory();

    // Auto-fill email/tenant from the default recent login
    useEffect(() => {
        if (selectedRecent) {
            setEmail(selectedRecent.email);
            setSelectedTenant(selectedRecent.tenantId);
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const auth = params.get('auth');
        const message = params.get('m');
        if (message) {
            setSuccessMessage(message);
        }
        if (auth) {
            setIsAuthenticating(true);
            loginWithAuth(auth)
                .then(response => {
                    if (response.status === 'success') {
                        AccountStorage.set({ token: response.api_token, user: response.user });
                        window.location.href = '/';
                    } else {
                        setErrorMessage(response.message || DEFAULT_MESSAGES.LOGIN_ERROR);
                        setIsAuthenticating(false);
                    }
                })
                .catch(error => {
                    setErrorMessage(DEFAULT_MESSAGES.LOGIN_ERROR);
                    setIsAuthenticating(false);
                });
        }
    }, [history]);

    const handleEmailBlur = async () => {
        setIsCheckingEmail(true);
        try {
            const data = await checkEmail(email);
            if (data.length === 0) {
                setEmailError('Email inválido ou não cadastrado');
                setSelectedTenant('');
                setTenants([]);
            } else if (data.length === 1) {
                setSelectedTenant(data[0].tenant_id);
                setEmailError('');
            } else {
                setTenants(data);
                setEmailError('');
            }
        } catch (error) {
            setErrorMessage(DEFAULT_MESSAGES.LOGIN_ERROR);
        } finally {
            setIsCheckingEmail(false);
        }
    };

    const handleSelectRecent = (item) => {
        setSelectedRecent(item);
        setEmail(item.email);
        setSelectedTenant(item.tenantId);
        setTenants([]);
        setEmailError('');
        setPassword('');
        setShowOtherAccounts(false);
    };

    const handleShowOtherAccounts = () => {
        setShowOtherAccounts(true);
        setSelectedRecent(null);
        setEmail('');
        setPassword('');
        setSelectedTenant('');
        setTenants([]);
        setEmailError('');
    };

    const handleRemoveRecent = (item) => {
        removeRecentLogin(item.email, item.tenantId);
        setRecentLogins(getRecentLogins());
    };

    const handleLogin = async (event) => {
        event.preventDefault();
        if (!email || !password) {
            setErrorMessage('Por favor, preencha o email e a senha.');
            return;
        }
        setIsAuthenticating(true);
        try {
            const data = await login(email, password, selectedTenant, getDispositivo(email, selectedTenant));
            if (data.dois_fatores) {
                setDesafio(data);
                return;
            }
            finalizarLogin(data);
        } catch (error) {
            if (error.response && error.response.status === 401) {
                setErrorMessage('Email ou senha inválido!');
            } else {
                setErrorMessage(DEFAULT_MESSAGES.LOGIN_ERROR);
            }
        } finally {
            setIsAuthenticating(false);
        }
    };

    const finalizarLogin = (data) => {
        saveRecentLogin({
            email: email,
            name: data.user.name,
            tenantId: selectedTenant,
            tenantName: data.user.tenant && data.user.tenant.name ? data.user.tenant.name : '',
        });
        if (data.dispositivo) {
            saveDispositivo(email, selectedTenant, data.dispositivo);
        }
        AccountStorage.set({ token: data.api_token, user: data.user });
        window.location.href = '/';
    };

    const voltarParaSenha = (mensagem) => {
        setDesafio(null);
        setPassword('');
        if (mensagem) setErrorMessage(mensagem);
    };

    const handleCloseSnackbar = () => {
        setErrorMessage('');
        setSuccessMessage('');
    };

    if (isAuthenticating) {
        return (
            <ThemeProvider theme={materialUITheme}>
                <Container>
                    <LoadingContainer>
                        <CircularProgress />
                        <p style={{marginTop: '5px'}}>Autenticando...</p>
                    </LoadingContainer>
                </Container>
            </ThemeProvider>
        );
    }

    if (desafio) {
        return (
            <ThemeProvider theme={materialUITheme}>
                <Container>
                    <Logo src={logo} alt="Logo" />
                    <VerificacaoCodigo desafio={desafio} onConfirmado={finalizarLogin} onVoltar={voltarParaSenha} />
                    <Snackbar
                        open={!!errorMessage}
                        autoHideDuration={6000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        ContentProps={{ style: { backgroundColor: 'red' } }}
                        message={errorMessage}
                    />
                </Container>
            </ThemeProvider>
        );
    }

    // Quick login: user selected a recent account, show password-only form
    if (selectedRecent) {
        return (
            <ThemeProvider theme={materialUITheme}>
                <Container>
                    <Logo src={logo} alt="Logo" />
                    <Form onSubmit={handleLogin}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 16 }}>
                            <Avatar style={{
                                backgroundColor: getAvatarColor(selectedRecent.name),
                                width: 56,
                                height: 56,
                                fontSize: 22,
                                fontWeight: 600,
                                marginBottom: 10
                            }}>
                                {getInitials(selectedRecent.name)}
                            </Avatar>
                            <div style={{ fontSize: 16, fontWeight: 600, color: '#262626' }}>{selectedRecent.name}</div>
                            <div style={{ fontSize: 13, color: '#8c8c8c' }}>{selectedRecent.email}</div>
                            {selectedRecent.tenantName && (
                                <div style={{ fontSize: 12, color: '#a0a0a0', marginTop: 2 }}>{selectedRecent.tenantName}</div>
                            )}
                        </div>
                        <MaterialUIFormControl>
                            <TextField
                                label="Senha"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                fullWidth
                                autoFocus
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                                size="small"
                                            >
                                                {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </MaterialUIFormControl>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={isAuthenticating}
                        >
                            {isAuthenticating ? 'ENTRANDO...' : 'ENTRAR'}
                        </Button>
                        <div style={{ textAlign: 'center', marginTop: 14 }}>
                            <a
                                onClick={handleShowOtherAccounts}
                                style={{ fontSize: 13, color: '#722ed1', cursor: 'pointer' }}
                            >
                                Usar outra conta
                            </a>
                        </div>
                    </Form>
                    <ForgotPasswordLink to="/esqueci-a-senha">Esqueceu a senha?</ForgotPasswordLink>
                    <Snackbar
                        open={!!errorMessage || !!successMessage}
                        autoHideDuration={6000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        ContentProps={{
                            style: { backgroundColor: errorMessage ? 'red' : 'green' }
                        }}
                        message={errorMessage || successMessage}
                    />
                </Container>
            </ThemeProvider>
        );
    }

    // Show recent logins when user clicked "Usar outra conta"
    var showRecentLogins = showOtherAccounts && recentLogins.length > 0;

    return (
        <ThemeProvider theme={materialUITheme}>
            <Container>
                <Logo src={logo} alt="Logo" />
                {showRecentLogins && (
                    <div style={{ width: 400, marginBottom: 16 }}>
                        <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                            Logins recentes
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {recentLogins.map(function(item) {
                                return React.createElement(RecentLoginCard, {
                                    key: item.email + '_' + item.tenantId,
                                    item: item,
                                    onSelect: handleSelectRecent,
                                    onRemove: handleRemoveRecent
                                });
                            })}
                        </div>
                        <div style={{ textAlign: 'center', marginTop: 12, marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: '#bfbfbf' }}>ou</span>
                        </div>
                    </div>
                )}
                <Form onSubmit={handleLogin}>
                    <MaterialUIFormControl>
                        <TextField
                            label="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value.trim())}
                            onBlur={handleEmailBlur}
                            fullWidth
                            error={!!emailError}
                            helperText={emailError}
                        />
                    </MaterialUIFormControl>
                    {tenants.length > 1 && (
                        <MaterialUIFormControl>
                            <TextField
                                select
                                label="Empresa"
                                value={selectedTenant}
                                onChange={(e) => setSelectedTenant(e.target.value)}
                                fullWidth
                                SelectProps={{
                                    native: true,
                                }}
                            >
                                <option value="" disabled>
                                    Selecione a empresa
                                </option>
                                {tenants.map((tenant) => (
                                    <option key={tenant.tenant_id} value={tenant.tenant_id}>
                                        {tenant.tenant_name}
                                    </option>
                                ))}
                            </TextField>
                        </MaterialUIFormControl>
                    )}
                    <MaterialUIFormControl>
                        <TextField
                            label="Senha"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                        >
                                            {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </MaterialUIFormControl>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={!selectedTenant || isCheckingEmail || isAuthenticating}
                    >
                        {isCheckingEmail ? 'VERIFICANDO...' : (isAuthenticating ? 'ENTRANDO...' : 'ENTRAR')}
                    </Button>
                </Form>
                <ForgotPasswordLink to="/esqueci-a-senha">Esqueceu a senha?</ForgotPasswordLink>
                <Snackbar
                    open={!!errorMessage || !!successMessage}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    ContentProps={{
                        style: { backgroundColor: errorMessage ? 'red' : 'green' }
                    }}
                    message={errorMessage || successMessage}
                />
            </Container>
        </ThemeProvider>
    );
};

export default LoginPage;
